import { useEffect } from 'react';
import { useFlags } from 'wsm-common-data';

import {
	getDOMElements,
	getDOMElementsLegacy,
	validateDOMElements,
	getHeaderConfig,
	getHeaderConfigLegacy,
	createSetVehicleToolbarVisibility,
	createVehicleCtasObserver,
	createResizeHandler,
	createAlertBannerObserver,
	createAlertBannerTopObserver,
	createAlertBannerTopObserverLegacy
} from '../utils';

/**
 * Custom hook to manage sticky vehicle toolbar behavior
 * Handles positioning, visibility, and responsiveness of the vehicle toolbar
 * based on scroll position, device type, and header configuration
*/
export const useStickyVehicleToolbar = () => {
	const flags = useFlags();
	const isTestFeatureEnabled = flags['ws-vehicle-toolbar-test-feature'] || false;

	useEffect(() => {
		const { DDC } = window;

		if (
			!DDC ||
			DDC.isComposer ||
			typeof window.IntersectionObserver === 'undefined'
		) {
			return undefined;
		}

		const dom = isTestFeatureEnabled ? getDOMElements() : getDOMElementsLegacy();

		// Validate required elements
		if (!validateDOMElements(dom)) {
			return undefined;
		}

		// Get configuration
		const config = (isTestFeatureEnabled ? getHeaderConfig : getHeaderConfigLegacy)({ dom, DDC });
		config.isLegacyDom = !isTestFeatureEnabled;

		// Create handlers
		const setVehicleToolbarVisibility = createSetVehicleToolbarVisibility({dom,config, DDC})

		// Create observers and handlers
		const vehicleCtasObserver = createVehicleCtasObserver({
			dom,
			config,
			DDC,
			setVehicleToolbarVisibility
		});

		const resizeHandler = createResizeHandler({
			DDC,
			setupVehicleCtasObserver: vehicleCtasObserver.setup
		});

		const alertBannerObserver = createAlertBannerObserver({
			dom,
			config,
			setupVehicleCtasObserver: vehicleCtasObserver.setup
		});

		const createAlertBannerTopObserverFn = config.isLegacyDom
			? createAlertBannerTopObserverLegacy
			: createAlertBannerTopObserver;
		const alertBannerTopObserver = createAlertBannerTopObserverFn({
			dom,
			setupVehicleCtasObserver: vehicleCtasObserver.setup,
		});

		// Initialize all
		vehicleCtasObserver.setup();
		resizeHandler.setup();
		alertBannerObserver.setup();
		alertBannerTopObserver.setup();

		// Cleanup function
		return () => {
			vehicleCtasObserver.cleanup();
			resizeHandler.cleanup();
			alertBannerObserver.cleanup();
			alertBannerTopObserver.cleanup();
		};
	}, [isTestFeatureEnabled]);
};